import type {
  AccessEventKind,
  AccessLogEntry,
} from '@/lib/api/generated/model';

import { VISIBILITY_NAME } from './accessCopy';

/**
 * What an access-log entry says, in a sentence (spec 191, service spec 157).
 *
 * Written about "the workspace" rather than naming it: in Settings the page already
 * names it, and the admins' log shows it in a column of its own. An entry of a kind
 * this build does not know reads as its kind, never as nothing.
 */

/** Who did it: a person, or the rollout that nobody did. */
function who(person: AccessLogEntry['actor']): string {
  return person?.display_name ?? 'Someone';
}

function text(detail: AccessLogEntry['detail'], key: string): string | null {
  const value = detail?.[key];
  return typeof value === 'string' ? value : null;
}

function count(detail: AccessLogEntry['detail'], key: string): number {
  const value = detail?.[key];
  return typeof value === 'number' ? value : 0;
}

/** Its visibility by the one name it has everywhere (spec 196 US4), if it is one. */
function visibilityName(value: string | null): string | null {
  return value === 'private' || value === 'organization'
    ? VISIBILITY_NAME[value]
    : null;
}

function role(value: string | null): string {
  return value === 'owner' ? 'an owner' : 'a member';
}

/** What an admin's owner-level change touched, from the route it was made on. */
const ADMIN_AREA: [string, string][] = [
  ['/secrets', 'a secret'],
  ['/connections', 'a connection'],
  ['/usage', 'the spend limits'],
  ['/concurrency', 'the concurrency limit'],
];

function adminArea(detail: AccessLogEntry['detail']): string {
  const route = text(detail, 'route') ?? '';
  return (
    ADMIN_AREA.find(([prefix]) => route.startsWith(prefix))?.[1] ??
    'an owner’s setting'
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Who the backfill found, besides the creator it made the owner. Never "0 people"
 * (spec 193 US7): a workspace nobody else had worked in has its creator alone.
 */
function workedHere(members: number): string {
  return members > 0
    ? plural(members, 'person', 'people')
    : 'no one besides its creator';
}

export function accessLogSentence(entry: AccessLogEntry): string {
  const actor = who(entry.actor);
  const subject = entry.subject?.display_name ?? 'someone';
  const detail = entry.detail;
  const kind: AccessEventKind = entry.kind;
  switch (kind) {
    case 'workspace_created': {
      const visibility = visibilityName(text(detail, 'visibility'));
      return visibility
        ? `${actor} created the workspace (${visibility})`
        : `${actor} created the workspace`;
    }
    case 'workspace_deleted':
      return `${actor} deleted the workspace`;
    case 'members_backfilled':
      return `Members set from who had worked here: ${workedHere(count(detail, 'members'))}; ${VISIBILITY_NAME.organization}`;
    case 'member_added':
      return text(detail, 'request_id')
        ? `${actor} added ${subject} as ${role(text(detail, 'role'))}, from their request`
        : `${actor} added ${subject} as ${role(text(detail, 'role'))}`;
    case 'member_removed':
      return `${actor} removed ${subject}`;
    case 'member_left':
      return `${subject} left`;
    case 'role_changed':
      return `${actor} made ${subject} ${role(text(detail, 'to'))}`;
    case 'visibility_changed': {
      const to = text(detail, 'to');
      const dropped = count(detail, 'follows_dropped');
      const base = `${actor} made the workspace ${VISIBILITY_NAME[to === 'private' ? 'private' : 'organization']}`;
      return dropped > 0
        ? `${base}; ${plural(dropped, 'follow', 'follows')} dropped`
        : base;
    }
    case 'renamed':
      return `${actor} renamed it from “${text(detail, 'from') ?? ''}” to “${text(detail, 'to') ?? ''}”`;
    case 'access_requested':
      return `${subject} asked to join`;
    case 'access_request_dismissed':
      return `${actor} dismissed ${subject}’s request to join`;
    case 'admin_opened':
      return `${actor} opened the workspace`;
    case 'admin_acted':
      return `${actor} changed ${adminArea(detail)}`;
    case 'members_review_requested':
      return 'Access was turned on; the owners were asked to check the members';
    default:
      return String(kind);
  }
}

/**
 * The mark beside an entry an org admin made with their own powers (decision Q7): a
 * chip, not part of the sentence (spec 194 US4), so the sentence reads the same
 * whoever made the change and the chip can be scanned for down the timeline.
 */
export const ORG_ADMIN_CHIP = {
  text: 'Org admin',
  title: 'Done with organization admin powers',
} as const;

/* ---------------------------------------------------------------------------
   What the Activity tab narrows to (spec 194 US4, FR-006).

   Client-side, over the pages already read: the log is read newest first a page
   at a time, and a filter that asked the service again would need a contract it
   does not have. An entry can be in two: adding someone from their request is
   both a change to the people and the answer to a request.
   --------------------------------------------------------------------------- */

export type AccessLogFilter =
  'all' | 'people' | 'settings' | 'requests' | 'admin';

export const ACCESS_LOG_FILTERS: readonly AccessLogFilter[] = [
  'all',
  'people',
  'settings',
  'requests',
  'admin',
];

export const ACCESS_LOG_FILTER_LABEL: Record<AccessLogFilter, string> = {
  all: 'All',
  people: 'People',
  settings: 'Visibility and name',
  requests: 'Requests',
  admin: 'Admin actions',
};

const PEOPLE_KINDS: ReadonlySet<AccessEventKind> = new Set<AccessEventKind>([
  'member_added',
  'member_removed',
  'member_left',
  'role_changed',
  'members_backfilled',
  'members_review_requested',
]);

const SETTINGS_KINDS: ReadonlySet<AccessEventKind> = new Set<AccessEventKind>([
  'workspace_created',
  'workspace_deleted',
  'visibility_changed',
  'renamed',
]);

const REQUEST_KINDS: ReadonlySet<AccessEventKind> = new Set<AccessEventKind>([
  'access_requested',
  'access_request_dismissed',
]);

export function inAccessLogFilter(
  entry: AccessLogEntry,
  filter: AccessLogFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'people':
      return PEOPLE_KINDS.has(entry.kind);
    case 'settings':
      return SETTINGS_KINDS.has(entry.kind);
    case 'requests':
      return (
        REQUEST_KINDS.has(entry.kind) ||
        (entry.kind === 'member_added' &&
          text(entry.detail, 'request_id') !== null)
      );
    case 'admin':
      return (
        entry.as_org_admin === true ||
        entry.kind === 'admin_opened' ||
        entry.kind === 'admin_acted'
      );
  }
}

/* ---------------------------------------------------------------------------
   The rollout, folded (spec 193 US7, FR-010).

   Turning access on writes two entries per workspace, neither by anyone: the
   backfill (`members_backfilled`) and the request that its owners check the
   result (`members_review_requested`). Listed as they are, they read as two
   events where there was one, and across an organization they bury everything
   else on the day: 63 workspaces made 126 rows.

   Two folds, in order:

   1. **Per workspace.** A workspace's review request and its backfill, both by
      nobody, become one row at the place of the newer (the review). Each is
      written once per workspace, so the pair needs no closer match than that.
      Either one alone — the backfill applied by the script before the switch
      went on, or the backfill on a page not read yet — keeps its own sentence.
   2. **Across workspaces** (the admins' log only). Rollout rows that follow one
      another in the list, with no other entry between them and none more than
      `ROLLOUT_GAP_MS` from the one before, are one rollout: the startup writes
      them in one transaction, the script in one run. A rollout of two or more
      workspaces becomes one row that expands to them; one workspace stays as
      its own row.
   --------------------------------------------------------------------------- */

/** How far apart two entries may be and still belong to the same rollout. */
export const ROLLOUT_GAP_MS = 10 * 60 * 1000;

const ROLLOUT_KINDS: ReadonlySet<AccessEventKind> = new Set<AccessEventKind>([
  'members_backfilled',
  'members_review_requested',
]);

function isRollout(entry: AccessLogEntry): boolean {
  return ROLLOUT_KINDS.has(entry.kind) && entry.actor == null;
}

/** One row of the log: an entry, or one workspace's two rollout entries as one. */
export interface AccessLogRow {
  /** The entry the row stands at (the newer, when two are folded). */
  entry: AccessLogEntry;
  /** Every entry the row says, newest first. */
  entries: AccessLogEntry[];
  sentence: string;
  /** Whether the row is the rollout's, which the organization fold groups. */
  rollout: boolean;
}

/** A rollout across workspaces, as one row that expands to them. */
export interface AccessLogRollout {
  id: string;
  /** When the newest of its entries was written. */
  at: string;
  sentence: string;
  /** One row per workspace, as the per-workspace fold reads them. */
  workspaces: AccessLogRow[];
}

export type AccessLogItem =
  ({ type: 'row' } & AccessLogRow) | ({ type: 'rollout' } & AccessLogRollout);

/** One workspace's rollout, both entries said at once. */
function rolloutSentence(backfilled: AccessLogEntry): string {
  const members = count(backfilled.detail, 'members');
  return members > 0
    ? `Access turned on: members set from who had worked here (${plural(members, 'person', 'people')}); owners asked to check them`
    : 'Access turned on: no one besides its creator had worked here; owners asked to check the members';
}

/** Fold 1: a workspace's two rollout entries into one row. */
export function foldWorkspaceRollout(
  entries: AccessLogEntry[],
): AccessLogRow[] {
  const consumed = new Set<string>();
  const rows: AccessLogRow[] = [];
  entries.forEach((entry, index) => {
    if (consumed.has(entry.id)) return;
    if (entry.kind === 'members_review_requested' && isRollout(entry)) {
      const backfilled = entries
        .slice(index + 1)
        .find(
          (older) =>
            older.kind === 'members_backfilled' &&
            isRollout(older) &&
            older.workspace.id === entry.workspace.id &&
            !consumed.has(older.id),
        );
      if (backfilled) {
        consumed.add(backfilled.id);
        rows.push({
          entry,
          entries: [entry, backfilled],
          sentence: rolloutSentence(backfilled),
          rollout: true,
        });
        return;
      }
    }
    rows.push({
      entry,
      entries: [entry],
      sentence: accessLogSentence(entry),
      rollout: isRollout(entry),
    });
  });
  return rows;
}

function organizationRolloutSentence(rows: AccessLogRow[]): string {
  const workspaces = new Set(rows.map((row) => row.entry.workspace.id)).size;
  const kinds = new Set(rows.flatMap((row) => row.entries.map((e) => e.kind)));
  const setUp = kinds.has('members_backfilled');
  const asked = kinds.has('members_review_requested');
  const many = plural(workspaces, 'workspace', 'workspaces');
  if (setUp && asked) {
    return `Access turned on: ${many} set up from who had worked in them; their owners were asked to check the members`;
  }
  if (setUp) {
    return `Access turned on: ${many} set up from who had worked in them`;
  }
  return `Access turned on: the owners of ${many} were asked to check the members`;
}

/**
 * The log as its rows: fold 1 always; fold 2 when the entries are many
 * workspaces' (`acrossWorkspaces`).
 */
export function foldAccessLog(
  entries: AccessLogEntry[],
  { acrossWorkspaces }: { acrossWorkspaces: boolean },
): AccessLogItem[] {
  const rows = foldWorkspaceRollout(entries);
  if (!acrossWorkspaces) return rows.map((row) => ({ type: 'row', ...row }));

  const items: AccessLogItem[] = [];
  let run: AccessLogRow[] = [];
  const flush = () => {
    const workspaces = new Set(run.map((row) => row.entry.workspace.id));
    if (workspaces.size >= 2) {
      const [newest] = run;
      items.push({
        type: 'rollout',
        id: `rollout-${newest.entry.id}`,
        at: newest.entry.at,
        sentence: organizationRolloutSentence(run),
        workspaces: run,
      });
    } else {
      run.forEach((row) => items.push({ type: 'row', ...row }));
    }
    run = [];
  };
  for (const row of rows) {
    const previous = run.at(-1);
    const close =
      previous !== undefined &&
      Math.abs(Date.parse(previous.entry.at) - Date.parse(row.entry.at)) <=
        ROLLOUT_GAP_MS;
    if (row.rollout && (previous === undefined || close)) {
      run.push(row);
      continue;
    }
    flush();
    if (row.rollout) run.push(row);
    else items.push({ type: 'row', ...row });
  }
  flush();
  return items;
}
